/**
 * advance-ai-cloaking.ts — v4M8.4a per-tick cloak decision helper.
 *
 * Pure function the v4M8.4b dispatch pass will call once per AI ship
 * per tick to decide whether to emit a `toggleCloak` command. Encodes
 * the rules locked at `docs/AI.md §5.4` against the per-archetype
 * `CloakingRuleRow` (with optional RACE-CC Phantom override) resolved
 * upstream by `composeEffectiveBehaviour`.
 *
 * Default behaviour per archetype:
 *   - Constructor (utility chassis): maintain cloak entering hostile
 *     territory; pre-build uncloak lead — drops cloak `constructorPre
 *     BuildUncloakLeadTicks` ticks before reaching the build site so
 *     build action starts cleanly on arrival.
 *   - Combat (light/mid/capital/squadron): cloak on entry to foreign
 *     territory; uncloak when first target enters weapon range
 *     (combat resolution begins). Uncloak in own / team territory
 *     to save energy.
 *   - Scout: stay cloaked in foreign territory regardless.
 *
 * RACE-CC Phantom Advanced-Cloaking override (`combatEngageUncloak =
 * false`): combat ship stays cloaked through engagement; the firing
 * energy drain still applies via the existing v3M5 Advanced Cloaking
 * weapon-fire path. Composed in `composeEffectiveBehaviour` (advance-
 * ai.ts) — no override-resolution code lives here.
 *
 * Pre-jump cloaked-fleet ambush (Brutal Phantom per AI.md §5.4) is
 * handled by v4M8.4d at the dispatch site, not in this per-tick
 * helper.
 *
 * Strict-parity scoping for v4M8.4a — the helper is exported and
 * tested, but no consumer reads it yet. v4M8.4b wires it into
 * `advance-ai.ts`'s per-tick loop.
 */

import { areTeammates, getTeamMembers } from './teams';
import type {
  Empire,
  EffectiveBehaviour,
  GameState,
  ShipInstance,
  SystemId,
} from '../state/types/index';

/** Per-tick cloak decision the dispatch pass acts on. */
export type CloakAction = 'cloak' | 'uncloak' | 'noop';

/**
 * Decide whether the ship should toggle its cloak this tick.
 *
 * Returns 'noop' for ships that don't carry the cloak mechanic at
 * all (template's `canCloak === false`), for mid-jump ships (cloak
 * state can't change in hyperspace), and for chassis classes outside
 * the §5.4 catalogue (colonisers, space structures, ring platforms).
 *
 * Pure — no state mutation. Called once per AI ship per AI tick in
 * v4M8.4b; tests in `advance-ai-cloaking.test.ts` lock the rule
 * branches.
 */
export function decideShipCloakAction(
  state: GameState,
  ship: ShipInstance,
  empire: Empire,
  behaviour: EffectiveBehaviour,
): CloakAction {
  // Template-driven gate — chassis without `canCloak` ignore the
  // mechanic entirely (no `toggleCloak` command would do anything).
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl?.canCloak) return 'noop';

  // Mid-jump ships have `systemId === null`. The cloak field still
  // exists but no decision applies until they re-emerge.
  if (ship.systemId === null) return 'noop';

  // v4M8.4d — charging-up ships are in a transitional state. The
  // dispatch may have intentionally pre-cloaked them (Brutal Phantom
  // ambush) and the per-tick helper shouldn't undo that, nor should
  // it flip cloak state repeatedly while the ship sits at the Jump
  // Area ring counting down. Wait for emergence at the destination
  // before re-evaluating. Saves energy on edge cases too — a charging
  // ship in own territory would otherwise uncloak (own-territory
  // rule) only to re-cloak on arrival in foreign territory.
  if (ship.chargeUpTicksRemaining > 0) return 'noop';

  const role = classifyChassisForCloak(ship.chassis);
  if (role === 'other') return 'noop';

  const inOwnTerritory = systemIsOwnOrTeamTerritory(state, empire, ship.systemId);
  const rule = behaviour.cloakingRule;

  if (role === 'constructor') {
    // Pre-build uncloak lead per §5.4. The ship's
    // `constructionOrder` is non-null while the Constructor is at the
    // build site executing — drop cloak immediately so the build
    // action runs without cloak interference. The lead-ticks figure
    // applies to the v4M8.4b dispatch-side queue rather than this
    // helper (Constructor on approach decides cloak via the foreign-
    // territory rule below; the moment the build starts, it's at the
    // site and this branch fires).
    if (ship.constructionOrder !== null) {
      return ship.cloaked ? 'uncloak' : 'noop';
    }
    // Default Constructor cloak gate: foreign territory → cloak,
    // own/team territory → uncloak.
    if (!inOwnTerritory && !ship.cloaked) return 'cloak';
    if (inOwnTerritory && ship.cloaked) return 'uncloak';
    return 'noop';
  }

  if (role === 'scout') {
    // Scout in foreign: cloak (rule-gated). No explicit "uncloak in
    // own territory" rule for scouts in §5.4 — leave them as-is so
    // a Scout that returned home keeps its current state. The dispatch
    // pass may flip them later if needed (e.g. dock for refit).
    if (!inOwnTerritory && rule.scoutInForeignCloak && !ship.cloaked) {
      return 'cloak';
    }
    return 'noop';
  }

  // role === 'combat'
  if (inOwnTerritory) {
    // Own territory: respect `combatInOwnCloak`. Default `false` →
    // uncloak to save energy. If the archetype prefers cloak even at
    // home, honour it (defensive ambush playstyle).
    if (!rule.combatInOwnCloak && ship.cloaked) return 'uncloak';
    if (rule.combatInOwnCloak && !ship.cloaked) return 'cloak';
    return 'noop';
  }

  // Foreign territory: cloak on entry per `combatInForeignCloak`.
  if (rule.combatInForeignCloak && !ship.cloaked) return 'cloak';

  // Combat-engage uncloak — only for archetypes where
  // `combatEngageUncloak === true`. RACE-CC Phantom (Advanced
  // Cloaking) sets this to `false` via the
  // `composeEffectiveBehaviour` override, so Phantom combat ships
  // stay cloaked through engagement.
  if (rule.combatEngageUncloak && ship.cloaked) {
    if (hasEnemyInWeaponRange(state, ship, empire, tmpl.visionRange)) {
      return 'uncloak';
    }
  }
  return 'noop';
}

/**
 * Bucket `ship.chassis` into the four §5.4 cloak categories. Mirrors
 * the regex set in `render/ship-draw.ts:chassisRoleFor` but kept
 * local to avoid the render-layer dependency from a sim file. The
 * cohort is locked at v1 — extending it (e.g. a new Class 3 cruiser)
 * means adding the chassis name to the regex here too.
 */
function classifyChassisForCloak(
  chassis: string,
): 'scout' | 'constructor' | 'combat' | 'other' {
  // Scouts: Scout (Terran) / Ranger (Kargath) / Probe (Reman).
  if (/^(Scout|Ranger|Probe)$/i.test(chassis)) return 'scout';
  // Constructor family: Engineer / Constructor / Mechanic.
  if (/^(Engineer|Constructor|Mechanic)$/i.test(chassis)) return 'constructor';
  // Carrier squadrons (synthetic chassis names ending in "Squadron").
  if (/Squadron$/i.test(chassis)) return 'combat';
  // Combat chassis cohort — light + mid + capital.
  if (
    /^(Corvette|Fighter|Infiltrator|Cruiser|Praetorian|Warship|Destroyer|Battleship|Dreadnought|Colossus|Carrier)$/i.test(
      chassis,
    )
  ) {
    return 'combat';
  }
  // Coloniser / space structures / other utility chassis: no cloak
  // rule applies.
  return 'other';
}

/**
 * Is `systemId` own or team-aligned territory for `empire`?
 *
 * "Territory" = the system contains at least one planet owned by the
 * empire OR a teammate. Unowned-planet systems do NOT count (would
 * make every neutral system "own", which defeats the foreign vs own
 * distinction §5.4 depends on).
 */
function systemIsOwnOrTeamTerritory(
  state: GameState,
  empire: Empire,
  systemId: SystemId,
): boolean {
  const system = state.systems.get(systemId);
  if (!system) return false;
  for (const planetId of system.planetIds) {
    const planet = state.planets.get(planetId);
    if (!planet) continue;
    if (planet.ownerEmpireId === null) continue;
    if (planet.ownerEmpireId === empire.id) return true;
    if (areTeammates(state, empire.id, planet.ownerEmpireId)) return true;
  }
  return false;
}

/**
 * Is there at least one enemy (non-team) live ship within `range` of
 * `ship` in the same system?
 *
 * Approximates the "combat resolution begins" trigger from §5.4 —
 * the actual first-fire trigger lives in `advance-combat.ts`, but the
 * cloak helper uses a slightly wider visibility radius so the cloak
 * drops a tick or two BEFORE first fire rather than precisely at it.
 * That gives the combat dispatch a fair shot at the target rather
 * than the ship's first weapon pulse landing while it's still
 * "invisible" by previous-tick visibility records.
 */
function hasEnemyInWeaponRange(
  state: GameState,
  ship: ShipInstance,
  empire: Empire,
  range: number,
): boolean {
  if (range <= 0) return false;
  const rangeSq = range * range;
  const teamMembers = new Set<number>(getTeamMembers(state, empire.id));
  for (const other of state.ships.values()) {
    if (other.id === ship.id) continue;
    if (other.systemId !== ship.systemId) continue;
    if (other.currentHull <= 0) continue;
    if (teamMembers.has(other.ownerEmpireId)) continue;
    const dx = other.sx - ship.sx;
    const dy = other.sy - ship.sy;
    if (dx * dx + dy * dy <= rangeSq) return true;
  }
  return false;
}
